import { useState, useCallback } from 'react';
import { message } from 'antd';
import { n301Api, N301ReportRow, N301SummaryRow, N301ReportParams } from '../../../services/n301Api';

/**
 * Auto-fill the "last" (end) value based on "start" + padding with 'z'.
 * BR-F01: dept 4 chars, BR-F04: cause 3 chars, BR-F07: customer 12 chars
 */
function autoFillEnd(start: string, maxLen: number): string {
  const trimmed = start.trim();
  if (!trimmed) {
    return 'z'.repeat(maxLen);
  }
  const padded = trimmed + 'z'.repeat(maxLen);
  return padded.substring(0, maxLen);
}

export function useN301Query() {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<N301ReportRow[]>([]);
  const [customerSummary, setCustomerSummary] = useState<N301SummaryRow[]>([]);
  const [causeSummary, setCauseSummary] = useState<N301SummaryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [filterDesc, setFilterDesc] = useState('');

  // Filter state
  const [startDate, setStartDate]           = useState('');
  const [lastDate, setLastDate]             = useState('');
  const [startCause, setStartCause]         = useState('');
  const [lastCause, setLastCause]           = useState('');
  const [startCustomer, setStartCustomer]   = useState('');
  const [lastCustomer, setLastCustomer]     = useState('');
  const [startDept, setStartDept]           = useState('');
  const [lastDept, setLastDept]             = useState('');
  const [caseStatus, setCaseStatus]         = useState<number>(3); // BR-P04: default to "all"

  // BR-F01: Auto-fill lastDept when startDept changes
  const handleStartDeptBlur = useCallback(() => {
    if (startDept.trim()) {
      setLastDept(autoFillEnd(startDept, 4));
    }
  }, [startDept]);

  // BR-F04: Auto-fill lastCause when startCause changes
  const handleStartCauseBlur = useCallback(() => {
    if (startCause.trim()) {
      setLastCause(autoFillEnd(startCause, 3));
    }
  }, [startCause]);

  // BR-F07: Auto-fill lastCustomer when startCustomer changes
  const handleStartCustomerBlur = useCallback(() => {
    if (startCustomer.trim()) {
      setLastCustomer(autoFillEnd(startCustomer, 12));
    }
  }, [startCustomer]);

  const handleQuery = useCallback(async () => {
    setLoading(true);
    try {
      const params: N301ReportParams = {
        startDate:     startDate     || undefined,
        lastDate:      lastDate      || undefined,
        startCause:    startCause    || undefined,
        lastCause:     lastCause     || undefined,
        startCustomer: startCustomer || undefined,
        lastCustomer:  lastCustomer  || undefined,
        startDept:     startDept     || undefined,
        lastDept:      lastDept      || undefined,
        caseStatus,
      };
      const res = await n301Api.query(params);
      setRecords(res.items || []);
      setCustomerSummary(res.customerSummary || []);
      setCauseSummary(res.causeSummary || []);
      setTotal(res.total || 0);
      setFilterDesc(res.filterDesc || '');
      if ((res.total || 0) === 0) {
        message.info('查無此資料!');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const errorMessage = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(errorMessage);
      setRecords([]);
      setCustomerSummary([]);
      setCauseSummary([]);
      setTotal(0);
      setFilterDesc('');
    } finally {
      setLoading(false);
    }
  }, [startDate, lastDate, startCause, lastCause, startCustomer, lastCustomer, startDept, lastDept, caseStatus]);

  return {
    loading, records, customerSummary, causeSummary, total, filterDesc,
    startDate, setStartDate,
    lastDate, setLastDate,
    startCause, setStartCause, handleStartCauseBlur,
    lastCause, setLastCause,
    startCustomer, setStartCustomer, handleStartCustomerBlur,
    lastCustomer, setLastCustomer,
    startDept, setStartDept, handleStartDeptBlur,
    lastDept, setLastDept,
    caseStatus, setCaseStatus,
    handleQuery,
  };
}
